import React from 'react';
import {
  Edit,
  NumberInput,
  BooleanInput,
  TextInput,
  TabbedForm,
  FormTab,
  ImageField,
  ImageInput,
  FileInput,
  FileField,
  maxLength,
  required,
  ArrayInput,
  SimpleFormIterator,
  minValue,
  maxValue,
  regex,
} from "react-admin";
import { RichTextInput } from "ra-input-rich-text";
import { CachedCategoriesInput } from "../common/CachedCategoriesInput/CachedCategoriesInput";

const ProductTitle = () => {
  return <span>Producto </span>;
};

const formatImages = (value) => {
  if (!value) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map((image) =>
      typeof image === "string"
        ? { src: image, title: image.split("/").pop() }
        : image
    );
  }
  return value;
};

const formatManual = (value) => {
  if (typeof value === "string") {
    return {
      src: value,
      title: value.split("/").pop(),
    };
  }
  return value;
};

export const ProductEdit = (props) => (
  <Edit
    title={<ProductTitle />}
    {...props}
    transform={(data) => {
      // Función para limpiar valores para el guardado
      const cleanValue = (value) => {
        if (!value) return null;
        if (value.rawFile instanceof File) {
          return value;
        }
        if (value.src) {
          return value.src;
        }
        if (typeof value === "string") {
          return value;
        }
        return null;
      };

      const cleanArray = (array) => {
        if (!Array.isArray(array)) return [];
        return array.map((item) => cleanValue(item)).filter(Boolean);
      };

      const categoryId = data.categoryId || data.category?.id || null;

      const ensureString = (value) => {
        if (value === null || value === undefined) return "";
        return value;
      };

      const ensureBoolean = (value) => !!value;

      const ensureNumber = (value) => {
        if (value === null || value === undefined || value === "") {
          return 0;
        }
        const num = parseFloat(value);
        return isNaN(num) ? 0 : num;
      };

      return {
        ...data,
        category: categoryId ? { id: categoryId } : null,
        categoryId: categoryId,
        // Asegurar que los campos de texto sean string vacío en lugar de null
        videoLink: ensureString(data.videoLink),
        featuredLink: ensureString(data.featuredLink),
        description: ensureString(data.description),
        catalogDescription: ensureString(data.catalogDescription),

        // Booleans
        enabled: ensureBoolean(data.enabled),
        featured: ensureBoolean(data.featured),
        featuredVideo: ensureBoolean(data.featuredVideo),

        // Numbers
        price: ensureNumber(data.price),
        stock: ensureNumber(data.stock),
        minMediumStock: ensureNumber(data.minMediumStock),
        minHighStock: ensureNumber(data.minHighStock),
        quantityPerPackage: ensureNumber(data.quantityPerPackage),
        totalReviews: ensureNumber(data.totalReviews),
        averageRating: ensureNumber(data.averageRating),

        // Mantener la lógica de limpieza para archivos
        manual: cleanValue(data.manual),
        images: cleanArray(data.images),
        hdImages: cleanArray(data.hdImages),
        catalogImages: cleanArray(data.catalogImages),
      };
    }}
  >
    <TabbedForm redirect="list">
      <FormTab label="General">
        <div
          style={{
            display: "flex",
            gap: "30px",
            flexDirection: "row",
            width: "100%",
          }}
        >
          <TextInput
            style={{ width: "50%" }}
            source="name"
            label="Nombre"
            validate={[required(), maxLength(100)]}
          />
          <TextInput
            source="slug"
            label="Slug"
            validate={[
              maxLength(255),
              regex(
                /^[a-z0-9-]+$/,
                "Solo se permiten letras minúsculas, números y guiones. Sin espacios."
              ),
            ]}
            style={{ width: "50%" }}
          />
        </div>
        <div
          style={{
            display: "flex",
            gap: "30px",
            flexDirection: "row",
            width: "100%",
          }}
        >
          <TextInput
            style={{ width: "25%" }}
            source="code"
            label="Código"
            validate={[required(), maxLength(10)]}
          />
          <CachedCategoriesInput
            style={{ width: "25%" }}
            source="categoryId"
            label="Categoría"
            perPage={100}
            sort={{ field: "name", order: "ASC" }}
            validate={[required()]}
          />
        </div>
        <div style={{ display: "flex", gap: "30px", flexDirection: "row" }}>
          <BooleanInput source="enabled" label="Habilitado" />
          <BooleanInput source="featured" label="Destacado" />
          <BooleanInput source="featuredVideo" label="Video Destacado" />
        </div>
        <TextInput
          source="videoLink"
          label="Enlace de Video"
          validate={[maxLength(200)]}
        />
        <div
          style={{
            display: "flex",
            gap: "30px",
            flexDirection: "row",
            width: "100%",
          }}
        >
          <NumberInput source="price" label="Precio" />
          <NumberInput source="stock" label="Stock" />
          <NumberInput source="minMediumStock" label="Stock Mínimo Medio" />
          <NumberInput source="minHighStock" label="Stock Mínimo Alto" />
          <NumberInput
            source="quantityPerPackage"
            label="Cantidad por Paquete"
          />
        </div>
        <TextInput
          source="description"
          label="Descripción"
          multiline
          style={{ display: "none" }}
        />
      </FormTab>
      <FormTab label="Características">
        <RichTextInput
          source="description"
          label="Descripción"
          validate={[required()]}
          options={{
            locale: "es-ES",
          }}
        />
        <RichTextInput
          source="catalogDescription"
          label="Descripción del Catálogo"
          validate={[maxLength(725)]}
          options={{
            locale: "es-ES",
          }}
        />
        <ArrayInput
          source="technicalSpecifications"
          label="Especificaciones técnicas"
        >
          <SimpleFormIterator>
            <div style={{ display: "flex", gap: "30px", flexDirection: "row" }}>
              <TextInput
                style={{ width: "200px" }}
                source="label"
                label="Etiqueta"
                validate={[required()]}
              />
              <TextInput
                style={{ width: "200px" }}
                source="value"
                label="Valor"
                validate={[required()]}
              />
            </div>
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>
      <FormTab label="Manual">
        <FileInput
          source="manual"
          label="Manual (PDF)"
          accept={{
            "application/pdf": [".pdf"],
          }}
          placeholder="Haz clic aquí o arrastra y suelta el manual (PDF)"
          format={formatManual}
          options={{
            preventDropOnDocument: true,
            maxFiles: 1,
            validator: (file) => {
              if (file.type !== "application/pdf") {
                return {
                  code: "invalid-file-type",
                  message: "Solo se permiten archivos PDF",
                };
              }
              return null;
            },
          }}
        >
          <FileField source="src" title="title" />
        </FileInput>
      </FormTab>
      <FormTab label="Imágenes">
        <ImageInput
          source="images"
          label="Imágenes"
          accept={{
            "image/*": [".png", ".jpg", ".jpeg", ".gif"],
          }}
          multiple
          placeholder="Haz clic aquí o arrastra y suelte las imágenes"
          format={formatImages}
          options={{
            preventDropOnDocument: true,
            validator: (file) => {
              if (!file.type.startsWith("image/")) {
                return {
                  code: "invalid-file-type",
                  message: "Solo se permiten imágenes",
                };
              }
              return null;
            },
          }}
        >
          <ImageField source="src" title="title" />
        </ImageInput>
        <ImageInput
          source="hdImages"
          label="Imágenes HD"
          accept={{
            "image/*": [".png", ".jpg", ".jpeg", ".gif"],
          }}
          multiple
          placeholder="Haz clic aquí o arrastra y suelte las imágenes"
          format={formatImages}
          options={{
            preventDropOnDocument: true,
            validator: (file) => {
              if (!file.type.startsWith("image/")) {
                return {
                  code: "invalid-file-type",
                  message: "Solo se permiten imágenes",
                };
              }
              return null;
            },
          }}
        >
          <ImageField source="src" title="title" />
        </ImageInput>
        <ImageInput
          source="catalogImages"
          label="Imágenes del Catálogo"
          accept={{
            "image/*": [".png", ".jpg", ".jpeg", ".gif"],
          }}
          multiple
          placeholder="Haz clic aquí o arrastra y suelte las imágenes"
          format={formatImages}
          options={{
            preventDropOnDocument: true,
            validator: (file) => {
              if (!file.type.startsWith("image/")) {
                return {
                  code: "invalid-file-type",
                  message: "Solo se permiten imágenes",
                };
              }
              return null;
            },
          }}
        >
          <ImageField source="src" title="title" />
        </ImageInput>
      </FormTab>
      <FormTab label="Opiniones">
        <div
          style={{
            display: "flex",
            gap: "30px",
            flexDirection: "row",
            borderBottom: "1px solid #ccc",
            width: "100%",
          }}
        >
          <NumberInput
            style={{ width: "200px" }}
            source="totalReviews"
            label="Total de Opiniones"
            min={0}
            validate={[minValue(0)]}
          />
          <NumberInput
            style={{ width: "200px" }}
            source="averageRating"
            label="Calificación Promedio"
            min={0}
            max={5}
            step={0.1}
            validate={[minValue(0), maxValue(5)]}
          />
        </div>
        <ArrayInput source="reviews" label="Opiniones de Usuarios">
          <SimpleFormIterator>
            <div style={{ display: "flex", gap: "30px", flexDirection: "row" }}>
              <TextInput
                style={{ width: "200px" }}
                source="author"
                label="Autor"
                validate={[required()]}
              />
              <NumberInput
                style={{ width: "200px" }}
                source="rating"
                label="Calificación"
                min={0}
                max={5}
                step={1}
                validate={[required(), minValue(0), maxValue(5)]}
              />
            </div>
            <TextInput
              source="comment"
              label="Comentario"
              multiline
              validate={[required()]}
            />
          </SimpleFormIterator>
        </ArrayInput>
      </FormTab>
    </TabbedForm>
  </Edit>
);
